import React from "react";
import themeGet from "@styled-system/theme-get";
import { Flex, FlexItem, H5, Box, Divider, Small } from "orcs-design-system";
import { NavLink } from "react-router-dom";
import styled from "styled-components";
import moment from "moment";
import useAIDashboards from "src/hooks/useAIDashboards";
import AIIcon from "src/components/AIContent/AI_Icon";
import { resolveAiDashboardPath } from "src/util/aiDashboardPath";

const DashboardLink = styled(NavLink)`
  text-decoration: none !important;
  font-size: ${themeGet("fontSizes.1")};
  padding: ${themeGet("space.s")};
  border-radius: ${themeGet("radii.2")};
  transition: ${themeGet("transition.transitionDefault")};
  color: ${themeGet("colors.bodyText")};
  display: flex;
  justify-content: space-between;

  &.active {
    background-color: ${themeGet("colors.navActiveBackground")};
    color: ${themeGet("colors.bodyText")};
  }

  &:hover {
    background-color: ${themeGet("colors.hoverSurface")};
    color: ${themeGet("colors.bodyText")};
  }
`;

/** Same layout as DashboardLink when there is no navigable path. */
const DashboardRow = styled(Box)`
  font-size: ${themeGet("fontSizes.1")};
  padding: ${themeGet("space.s")};
  border-radius: ${themeGet("radii.2")};
  color: ${themeGet("colors.mutedText")};
  display: flex;
  justify-content: space-between;
`;

const DashboardMeta = ({ createdAt }) => (
  <Small
    minWidth="115px"
    fontSize="0.75"
    ml="auto"
    textAlign="right"
    color="mutedText"
  >
    {moment(createdAt).fromNow()}
  </Small>
);

const AIDashboardsContent = () => {
  const { dashboards, loading } = useAIDashboards();

  if (loading) {
    return (
      <Flex
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100%"
        p="r"
      >
        <Small color="greyMedium">Loading dashboards...</Small>
      </Flex>
    );
  }

  if (!dashboards || dashboards.length === 0) {
    return (
      <Flex
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100%"
        p="r"
      >
        <Box mb="s">
          <AIIcon width={48} height={48} />
        </Box>
        <Small color="greyMedium" textAlign="center">
          No AI-generated dashboards yet. Ask the AI to create a dashboard in a
          chat!
        </Small>
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" height="100%">
      <FlexItem>
        <Box mb="r">
          <Flex alignItems="center" gap="xs">
            <AIIcon width={20} height={20} />

            <H5 fontWeight="bold" data-testid="heading">
              Dashboards
            </H5>
          </Flex>

          <Box my="r">
            <Divider light />
          </Box>
          <Flex flexDirection="column" gap="xs">
            {dashboards.map(({ id, title, aiChatId, createdAt, format }) => {
              const dashboardLink = resolveAiDashboardPath({
                dashboardId: id,
                chatId: aiChatId,
                format,
              });

              if (!dashboardLink) {
                // v1 without aiChatId — no legacy route; don't send users to v2.
                return (
                  <DashboardRow
                    key={id}
                    title="Dashboard is missing its chat link"
                  >
                    <span>{title} </span>
                    <DashboardMeta createdAt={createdAt} />
                  </DashboardRow>
                );
              }

              return (
                <DashboardLink key={id} to={dashboardLink}>
                  <span>{title} </span>
                  <DashboardMeta createdAt={createdAt} />
                </DashboardLink>
              );
            })}
          </Flex>
        </Box>
      </FlexItem>
    </Flex>
  );
};

export default AIDashboardsContent;
